import { ThemeProvider } from '@emotion/react';
import {
  StyledEngineProvider,
  GlobalStyles,
  Stack,
  Box,
  Typography,
} from '@mui/material';
import { theme } from './common/theme';
import './logged-out.css';
import { images } from './assets/images';
import { appEnvironment } from './common/environment';
import CheckoutForm from './components_v2/forms/CheckoutForm';
import SYDLeftPanelContainer from './components_v2/SYDLeftPanelContainer';
import HeaderProgressBar from './components_v2/header/HeaderPogressBar';
import ActionFooter from './components_v2/logged_out/ActionFooter';
import FormContent from './components_v2/logged_out/FormContent';
import { useLoggedOutLogic } from './components_v2/logged_out/hooks/useLoggedOutLogic';

export default function LoggedOutV2() {
  const {
    t,
    searchParams,
    onboardingStep,
    buttonEnabled,
    loading,
    showingMessages,
    isAnimatingPhase,
    leftPanelImageRef,
    formRef,
    leftPanelTranslationX,
    headerData,
    stepTitle,
    handleContinue,
  } = useLoggedOutLogic();

  return (
    <StyledEngineProvider injectFirst={true}>
      <ThemeProvider theme={theme}>
        <GlobalStyles
          styles={{ body: { background: '#F5F6F6', padding: 0, margin: 0 } }}
        />

        <Stack
          className="root-container"
          sx={{
            minHeight: '100vh',
            boxSizing: 'border-box',
          }}
        >
          {/* Header Section */}
          <Stack className="relative w-full justify-center my-[20px] z-20">
            <Stack
              justifyContent="center"
              alignItems="center"
              className="absolute left-[40px] top-0 logo-container"
            >
              <Box
                component="img"
                src={appEnvironment.theme.images.logo}
                className="max-w-[50px] macbook13:max-w-[40px]"
              />
            </Stack>

            <Box
              className={
                headerData.show ? 'opacity-100' : 'opacity-0 invisible'
              }
            >
              <HeaderProgressBar
                label={t('signUp')}
                step={headerData.step}
                totalSteps={2}
                subtext={headerData.subtext}
                blue={onboardingStep === 'SUMMARY'}
                stepTitle={stepTitle}
              />
            </Box>
          </Stack>

          {/* Main Area */}
          <Stack direction="row" className="w-full flex-1 relative">
            {searchParams.get('payment_intent') !== null ? (
              <CheckoutForm />
            ) : (
              <>
                {appEnvironment.theme.showOnboardingAnimation && (
                  <Stack
                    className={`fixed top-0 left-0 w-full h-full z-[-2] ${
                      !isAnimatingPhase
                        ? ''
                        : 'transition-transform duration-1000'
                    }`}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ transform: `translateX(${leftPanelTranslationX}px)` }}
                  >
                    <SYDLeftPanelContainer imageRef={leftPanelImageRef} />
                  </Stack>
                )}

                {!isAnimatingPhase && (
                  <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    className="relative z-10 w-full mb-[10px] min-h-full flex-1"
                  >
                    <Stack
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                      className="macbook13:mt-[0px] w-[700px]"
                    >
                      <FormContent step={onboardingStep} formRef={formRef} />
                    </Stack>
                  </Stack>
                )}
              </>
            )}
          </Stack>

          {/* Footer Action Area */}
          <ActionFooter
            step={onboardingStep}
            isAnimatingPhase={isAnimatingPhase}
            loading={loading}
            showingMessages={showingMessages}
            buttonEnabled={buttonEnabled}
            onContinue={handleContinue}
            t={t}
          />

          {/* Bottom Bar */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            className="w-full px-[40px] pb-[20px] pt-[16px] mt-auto z-10"
          >
            {/* Left: Security Logos */}
            <Box
              component="img"
              src={images.v2.onboarding.securityLogos}
              className="h-[40px]"
            />

            {/* Center: 98% of members note (only shown once animation completes) */}
            {!isAnimatingPhase ? (
              <Typography
                component="span"
                className="text-[13px] font-medium text-center"
                sx={{ color: '#757D87' }}
              >
                <Typography
                  component="span"
                  sx={{
                    color: appEnvironment.theme.colours.mint,
                    fontWeight: 600,
                    fontSize: '13px',
                  }}
                >
                  98%
                </Typography>{' '}
                {t('percentOfMembers').replace(
                  /syd/gi,
                  appEnvironment.theme.appName
                )}
              </Typography>
            ) : (
              <Box />
            )}

            {/* Right: Rating Badges */}
            <Box
              component="img"
              src={images.v2.onboarding.ratingLogos}
              className="h-[40px]"
            />
          </Stack>
        </Stack>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
